(function () {
	/**
	 * Forces unfinished toast timers to finalize and logs the result.
	 */
	function finalizeActiveToasts() {
		if (Array.isArray(window.toastTimerLogs)) {
			const unfinishedToasts = window.toastTimerLogs.filter((entry) => entry.endTime === null);
			let forcedFinalizations = false;
			if (unfinishedToasts.length > 0) {
				console.warn('[FEEDBACK] Forcibly finalizing unfinished toast timers:');
				unfinishedToasts.forEach((entry) => {
					entry.endTime = Date.now();
					entry.durationMs = entry.startTime ? entry.endTime - entry.startTime : null;
					entry.event = entry.event === 'Started' ? 'Forcibly Fired' : entry.event;
					console.warn(`- Timer ${entry.timerId} forcibly finalized`);
				});
				forcedFinalizations = true;
			}
			if (forcedFinalizations) {
				console.warn('[FEEDBACK] Forced finalizations detected — some timers were manually closed.');
			} else {
				console.log('[FEEDBACK] No forced finalizations — all timers completed naturally.');
			}
		}
	}

	window.toastTimerLogs = [];
	if (window.location.pathname !== '/account') {
		console.log('[FEEDBACK] Not on /account — skipping loading feedback timers and toasts');
		return;
	}

	// Fallback: If guardianUserConfirmed doesn't fire quickly, preemptively publish it
	window.guardianToastPrecheckStarted = false;
	const guardianPrecheckFallbackTimer = setTimeout(() => {
		if (!window.guardianToastPrecheckStarted) {
			// Extra safeguard: only fallback if guardianData is missing or incomplete
			const noGuardianDataYet = !window.guardianData || Object.keys(window.guardianData).length === 0;
			const isAccountPage = window.location.pathname === '/account';
			if (noGuardianDataYet && isAccountPage) {
				console.warn(
					'[FEEDBACK] Pre-emptive guardianUserConfirmed fallback triggered (likely early page load, no guardianData)'
				);
				window.guardianToastPrecheckStarted = true;
				publish(PUB_SUB_EVENTS.guardianUserConfirmed);
			} else {
				console.log('[FEEDBACK] Skipped pre-emptive fallback: guardianData already exists');
			}
		}
	}, 3000); // Increased to 3 seconds

	/**
	 * Logs a toast timer event, updating start and end times based on event type.
	 * @param {string} eventName - The name of the event ('start', 'fire', or 'cancel').
	 * @param {number} timerId - The ID of the timer associated with the event.
	 */
	function logToastTimer(eventName, timerId) {
		const now = Date.now();

		if (eventName === 'start') {
			window.toastTimerLogs.push({
				timerId,
				event: 'Started',
				startTime: now,
				endTime: null,
				durationMs: null
			});
		} else if (eventName === 'fire') {
			const logEntry = window.toastTimerLogs.find((entry) => entry.timerId === timerId && entry.endTime === null);
			if (logEntry) {
				logEntry.endTime = now;
				logEntry.durationMs = now - logEntry.startTime;
				logEntry.event = 'Fired';
			} else {
				window.toastTimerLogs.push({
					timerId,
					event: 'Fired (no start found)',
					startTime: null,
					endTime: now,
					durationMs: null
				});
			}
		} else if (eventName === 'cancel') {
			const logEntry = window.toastTimerLogs.find((entry) => entry.timerId === timerId && entry.endTime === null);
			if (logEntry) {
				logEntry.endTime = now;
				logEntry.durationMs = now - logEntry.startTime;
				logEntry.event = 'Cancelled';
			} else {
				console.warn(`[FEEDBACK] Cancelling unknown timerId: ${timerId}`);
				window.toastTimerLogs.push({
					timerId,
					event: 'Cancelled (no start found)',
					startTime: null,
					endTime: now,
					durationMs: null
				});
			}
		}
	}

	const guardianToastDelay = 1000;
	const guardianTimeoutDelay = 2500;

	const start = performance.now();
	console.log(`[FEEDBACK] Timers initialized at ${(performance.now() - start).toFixed(1)}ms`);
	let toastShown = false;
	let guardianDataSetHandled = false;
	let doubleCheckTimer = null;
	let toastId = null;
	let toastShownAt = null;
	const minToastVisibleTime = 3000; // milliseconds

	/**
	 * Checks if the guardian data has meaningful stats that indicate progress or activity.
	 * @returns {boolean} True if meaningful stats are present, otherwise false.
	 */
	// Utility to determine if data is real
	const hasRealStats = () => {
		const data = window.guardianData;
		if (!data) return false;

		return (
			(data.good_points && data.good_points.available > 0) ||
			(data.impact && (data.impact.animals_fed > 0 || data.impact.people_fed > 0 || data.impact.trees_planted > 0)) ||
			data.petitions_signed > 0 ||
			(data.redemptions && data.redemptions.length > 0)
		);
	};

	// Schedule delayed toast only after user is confirmed logged in
	subscribe(PUB_SUB_EVENTS.guardianUserConfirmed, () => {
		window.guardianToastPrecheckStarted = true;
		clearTimeout(guardianPrecheckFallbackTimer);

		// Wait a tick to allow guardianUserIsLoggedIn setter to run
		setTimeout(() => {
			if (window.guardianData?.userIsLoggedIn !== true) {
				console.log('[FEEDBACK] Delayed toast logic deferred — login state still not true');
				// Poll until login is true before evaluating
				const interval = setInterval(() => {
					if (window.guardianData?.userIsLoggedIn === true) {
						clearInterval(interval);
						console.log('[FEEDBACK] Deferred toast logic resuming — login state now true');
						if (!hasRealStats() && !toastShown) {
							toastShown = true;
							console.log(
								`[FEEDBACK] Delayed toast fired (${((performance.now() - start) / 1000).toFixed(1)}s elapsed)`
							);
							publish(PUB_SUB_EVENTS.guardianLoadingDelayed);
						}
					}
				}, 50);
			} else {
				console.log('[FEEDBACK] Login state already true — checking toast');
				if (!hasRealStats() && !toastShown) {
					toastShown = true;
					console.log(`[FEEDBACK] Delayed toast fired (${((performance.now() - start) / 1000).toFixed(1)}s elapsed)`);
					publish(PUB_SUB_EVENTS.guardianLoadingDelayed);
				}
			}
		}, guardianToastDelay);

		// Extra safeguard: recheck after 20s even if timers are cleared
		doubleCheckTimer = setTimeout(() => {
			logToastTimer('fire', doubleCheckTimer);
			if (
				!guardianDataSetHandled &&
				window.guardianData?.userIsLoggedIn === true &&
				!hasRealStats() &&
				!toastShown &&
				window.location.pathname === '/account'
			) {
				toastShown = true;
				console.warn('[FEEDBACK] Double-check fallback toast triggered at 20s');
				publish(PUB_SUB_EVENTS.guardianLoadingDelayed);
			}
		}, 20000);
		logToastTimer('start', doubleCheckTimer);
	});

	let timeoutTimer = null;
	subscribe(PUB_SUB_EVENTS.guardianUserConfirmed, () => {
		window.guardianToastPrecheckStarted = true;
		clearTimeout(guardianPrecheckFallbackTimer);

		timeoutTimer = setTimeout(() => {
			logToastTimer('fire', timeoutTimer);
			if (window.guardianData?.userIsLoggedIn === true && !hasRealStats() && window.location.pathname === '/account') {
				console.log(`[FEEDBACK] Timeout fired (${((performance.now() - start) / 1000).toFixed(1)}s elapsed)`);
				publish(PUB_SUB_EVENTS.guardianLoadingTimeout);
			}
		}, guardianTimeoutDelay);
		logToastTimer('start', timeoutTimer);
	});

	/**
	 * Dismisses the active toast once its cause has resolved, honouring the minimum visible time.
	 *
	 * This deliberately ignores the toast's duration. The loading toast is always created with
	 * 'infinite', so gating dismissal on the duration meant it could never be dismissed at all.
	 */
	function scheduleToastDismissal() {
		if (!toastId || !CusaToastNotification?.dismiss) return;

		const timeSinceToastShown = toastShownAt ? performance.now() - toastShownAt : Infinity;
		const delay = Math.max(minToastVisibleTime - timeSinceToastShown, 0);

		const timerId = setTimeout(() => {
			logToastTimer('fire', timerId);
			if (CusaToastNotification?.dismiss && toastId) {
				CusaToastNotification.dismiss(toastId);
				toastId = null;
				toastShownAt = null;
			}
		}, delay);
		logToastTimer('start', timerId);
	}

	// Handle data arriving late
	subscribe(PUB_SUB_EVENTS.guardianDataSet, () => {
		if (guardianDataSetHandled) return;
		guardianDataSetHandled = true;

		const timeElapsed = performance.now() - start;
		console.log(`[FEEDBACK] Data set received (${(timeElapsed / 1000).toFixed(1)}s elapsed), clearing timers`);
		logToastTimer('cancel', timeoutTimer);
		clearTimeout(timeoutTimer);
		logToastTimer('cancel', doubleCheckTimer);
		clearTimeout(doubleCheckTimer);

		if (
			timeElapsed > guardianToastDelay &&
			!toastShown &&
			!hasRealStats() &&
			window.guardianData?.userIsLoggedIn === true
		) {
			toastShown = true;
			console.log('[FEEDBACK] Showing delayed toast on late guardianDataSet');
			publish(PUB_SUB_EVENTS.guardianLoadingDelayed);
		}

		console.log('[FEEDBACK] guardianDataSet received — evaluating toast dismissal');
		if (hasRealStats()) scheduleToastDismissal();
	});

	// Subscribe to guardianLoadingDelayed event and show toast notification
	subscribe(PUB_SUB_EVENTS.guardianLoadingDelayed, () => {
		console.log('[FEEDBACK] Received guardianLoadingDelayed event');
		console.log('[DEBUG] guardianUserIsLoggedIn:', window.guardianData?.userIsLoggedIn);
		if (window.guardianData?.userIsLoggedIn !== true) {
			console.log('[FEEDBACK] Skipping toast: user not logged in or login state unknown');
			return;
		}
		if (window.location.pathname !== '/account') {
			console.log('[FEEDBACK] Skipping toast: not on /account page');
			return;
		}
		toastShownAt = performance.now();
		if (window.CusaToastNotification) {
			console.log('[FEEDBACK] Creating loading toast now');
			toastId = CusaToastNotification.create({
				message: "We're doing our best to load your stats. If you've just signed up, please check your email to activate your account.",
				type: 'info',
				duration: 'infinite'
			});
		}
	});

	// Subscribe to guardianFetchFailed event and show toast notification
	subscribe(PUB_SUB_EVENTS.guardianFetchFailed, () => {
		console.warn('[FEEDBACK] Received guardianFetchFailed event');
		console.log('[DEBUG] guardianUserIsLoggedIn:', window.guardianData?.userIsLoggedIn);
		const canShowGuardianFetchFailedToast =
			window.guardianData?.userIsLoggedIn === true ||
			(window.guardianData?.userIsLoggedIn === undefined && window.location.pathname === '/account');

		if (!canShowGuardianFetchFailedToast) {
			console.warn('[FEEDBACK] Skipping toast for guardianFetchFailed: user not logged in or login state unknown');
			return;
		}
		// Log if fallback (undefined + /account) was triggered
		if (window.guardianData?.userIsLoggedIn === undefined && window.location.pathname === '/account') {
			console.log('[FEEDBACK] guardianFetchFailed toast fallback triggered: userIsLoggedIn undefined but on /account');
		}
		if (toastShown) {
			console.log('[FEEDBACK] Skipping guardianFetchFailed toast: toast already shown');
			return;
		}
		toastShown = true;
		toastShownAt = performance.now();
		if (window.CusaToastNotification) {
			console.log('[FEEDBACK] Creating loading toast for guardianFetchFailed');
			toastId = CusaToastNotification.create({
				message: "We're doing our best to load your stats. If you've just signed up, please check your email to activate your account.",
				type: 'info',
				duration: 'infinite'
			});

			// Stats are often already on screen when a later request fails. guardianDataSet has
			// fired by then and will not fire again, so this branch has to clear its own toast.
			if (hasRealStats()) scheduleToastDismissal();
		}
	});

	setTimeout(() => {
		finalizeActiveToasts();
		if (window.toastTimerLogs.length > 0) {
			const excludedEntries = window.toastTimerLogs.filter((entry) => entry.event === 'Cancelled (no start found)');
			if (excludedEntries.length > 0) {
				console.info(
					`[FEEDBACK] ${excludedEntries.length} 'Cancelled (no start found)' timer(s) excluded from summary`
				);
			}
			console.groupCollapsed('%c🔥 Toast Timer Events Summary', 'color: orange; font-weight: bold;');
			console.table(
				window.toastTimerLogs
					.filter((entry) => entry.event !== 'Cancelled (no start found)')
					.map((entry) => ({
						Timer: entry.timerId,
						Event: entry.event,
						Duration: entry.durationMs !== null ? `${entry.durationMs} ms` : 'n/a'
					}))
			);
			console.groupEnd();
		} else {
			console.log('No toast timer events captured.');
		}
	}, 5000);
})();
